import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from './Icon'
import { useEscape } from '../useEscape'

/**
 * The sentence a field needs, next to the field, on request.
 *
 * A form asks a question and then leaves people to guess what the answer is
 * for: what "Order" does when the navigation decides the order anyway, what
 * "Recheck" costs, whether "Group" is a folder. The answers existed — in the
 * heads of the people who built it, and occasionally in a `title` only a
 * mouse could read.
 *
 * A note under every field would be a wall of grey text nobody reads, and a
 * tooltip would put a paragraph somewhere it cannot be selected or kept open.
 * So this is a button beside the label that opens a small panel: closed by
 * default, one press away, and it stays open while it is being read.
 *
 * It is a popover rather than a dialog on purpose — nothing behind it is
 * suspended, and Escape closes only this.
 */
export function FieldHelp({ says, label }: { says: string; label: string }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const host = useRef<HTMLSpanElement>(null)

  /*
   * Only this: a popover is not a dialog, and closing the drawer behind it as
   * well is the opposite of what Escape was pressed for. `useEscape` is what
   * makes that true rather than hoped for — it hands the keystroke to whatever
   * opened last, which while this is open is this.
   */
  useEscape(open, () => setOpen(false))

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!host.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <span className="fieldhelp" ref={host}>
      <button
        type="button"
        className="fieldhelp__ask"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="info" size={12} label={`What ${label} is for`} />
      </button>
      {open && (
        <span className="fieldhelp__panel" id={id} role="note">
          {says}
        </span>
      )}
    </span>
  )
}
